import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StatCard } from '@/components/StatCard'
import { StatCardSkeleton, ErrorState } from '@/components/States'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { SimpleChart } from '@/components/SimpleChart'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import {
  IconOrganizations,
  IconUsers,
  IconDocuments,
  IconClock,
  IconSignatures,
  IconActivity,
} from '@/components/Icons'
import { useActivity, useAnalytics, useDashboard, useSecuritySummary } from '@/hooks/useRootQueries'
import type { AuditEvent } from '@/types/api'

export function Dashboard() {
  const navigate = useNavigate()
  const dashboard = useDashboard()
  const recentActivity = useActivity({ page: 1, pageSize: 6 })
  const security = useSecuritySummary()
  const analytics = useAnalytics('7d')

  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)
  const [docDrawerOpen, setDocDrawerOpen] = useState(false)

  const totalDocs = dashboard.data?.totalDocuments ?? 0
  const completedDocs = dashboard.data?.completedDocuments ?? 0
  const pendingDocs = dashboard.data?.pendingDocuments ?? 0
  const declinedDocs = dashboard.data?.declinedDocuments ?? 0

  const completedPct = totalDocs > 0 ? Math.round((completedDocs / totalDocs) * 100) : 0
  const pendingPct = totalDocs > 0 ? Math.round((pendingDocs / totalDocs) * 100) : 0
  const declinedPct = totalDocs > 0 ? Math.round((declinedDocs / totalDocs) * 100) : 0

  function handleTimelineSelect(event: AuditEvent) {
    setSelectedEvent(event)
    setDocDrawerOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <span>Platform Overview</span>
          </h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Multi-tenant governance, cryptographic signing telemetry &amp; live cluster analytics.
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => navigate('/organizations')}
            className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:border-sky-300 hover:text-sky-700 hover:bg-sky-50/30 transition-all flex items-center gap-1.5"
          >
            <IconOrganizations className="h-3.5 w-3.5 text-slate-500" />
            <span>Organizations</span>
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:border-sky-300 hover:text-sky-700 hover:bg-sky-50/30 transition-all flex items-center gap-1.5"
          >
            <IconDocuments className="h-3.5 w-3.5 text-slate-500" />
            <span>Documents</span>
          </button>
          <div className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 font-mono text-[11px] font-bold text-emerald-700 shadow-2xs">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-600" />
            Live Sync
          </div>
        </div>
      </div>

      {/* Top Metrics Grid */}
      {dashboard.isError && (
        <ErrorState
          message="Could not load platform statistics from backend API."
          onRetry={() => dashboard.refetch()}
        />
      )}

      {!dashboard.isError && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {dashboard.isLoading || !dashboard.data ? (
            Array.from({ length: 6 }).map((_, i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              <StatCard
                label="Organizations"
                icon={<IconOrganizations className="h-4 w-4" />}
                value={dashboard.data.totalOrganizations}
                delta={`${dashboard.data.activeOrganizations} active`}
                tone="up"
              />
              <StatCard
                label="Platform Users"
                icon={<IconUsers className="h-4 w-4" />}
                value={dashboard.data.totalUsers}
                delta={`${dashboard.data.activeUsers} active`}
                tone="up"
              />
              <StatCard
                label="Total Documents"
                icon={<IconDocuments className="h-4 w-4" />}
                value={dashboard.data.totalDocuments}
                delta={`${dashboard.data.documentsToday} today`}
                tone="up"
              />
              <StatCard
                label="Pending Signers"
                icon={<IconClock className="h-4 w-4" />}
                value={dashboard.data.pendingDocuments}
                delta={dashboard.data.pendingDocuments > 0 ? "Awaiting actions" : "Up to date"}
                tone={dashboard.data.pendingDocuments > 0 ? "warn" : "neutral"}
              />
              <StatCard
                label="Signatures"
                icon={<IconSignatures className="h-4 w-4" />}
                value={dashboard.data.totalSignatures}
                delta={`${dashboard.data.signaturesToday} today`}
                tone="up"
              />
              <StatCard
                label="Events Today"
                icon={<IconActivity className="h-4 w-4" />}
                value={dashboard.data.activityEventsToday}
                delta="Audit stream"
                tone="up"
              />
            </>
          )}
        </div>
      )}

      {/* Middle Analytics & Control Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column (2 Cols): Analytics Chart & Doc Breakdown */}
        <div className="space-y-6 lg:col-span-2">
          {/* 7-Day Trend Chart Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  7-Day Platform Activity Volume
                </h2>
                <p className="text-[11px] font-medium text-slate-500">
                  Document creation, recipient signing &amp; audit velocity across tenant organizations
                </p>
              </div>
              <button
                onClick={() => navigate('/analytics')}
                className="text-xs font-bold text-sky-600 hover:text-sky-800 transition-colors"
              >
                Full Analytics →
              </button>
            </div>
            <div className="pt-2">
              {analytics.isLoading ? (
                <div className="h-44 animate-pulse rounded-lg bg-slate-100 mt-4" />
              ) : (
                <SimpleChart points={analytics.data?.series ?? []} />
              )}
            </div>
          </div>

          {/* Document Lifecycle & Status Ratio Card */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Document Lifecycle Distribution
                </h2>
                <p className="text-[11px] font-medium text-slate-500">
                  Breakdown of active platform documents by execution status
                </p>
              </div>
              <button
                onClick={() => navigate('/documents')}
                className="text-xs font-bold text-sky-600 hover:text-sky-800 transition-colors"
              >
                View Documents →
              </button>
            </div>
            <div className="mt-4 space-y-3.5">
              <ProgressBar label="Completed & Signed Documents" count={completedDocs} pct={completedPct} color="bg-emerald-500" />
              <ProgressBar label="Pending & In-Progress Signers" count={pendingDocs} pct={pendingPct} color="bg-amber-500" />
              <ProgressBar label="Declined / Expired / Voided" count={declinedDocs} pct={declinedPct} color="bg-rose-500" />
            </div>
          </div>

          {/* Recent Activity Card */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/80 px-5 py-3.5">
              <div className="flex items-center gap-2">
                <span className="flex h-2 w-2 rounded-full bg-sky-600 animate-pulse" />
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Live Audit Activity
                </h2>
              </div>
              <button
                onClick={() => navigate('/activity')}
                className="text-xs font-bold text-sky-600 hover:text-sky-800 transition-colors"
              >
                View Full Feed →
              </button>
            </div>
            <div className="px-5 py-5">
              {recentActivity.isError ? (
                <ErrorState message="Could not load recent activity." onRetry={() => recentActivity.refetch()} />
              ) : recentActivity.isLoading ? (
                <div className="animate-pulse space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-3 w-full rounded bg-slate-200" />
                  ))}
                </div>
              ) : (
                <ActivityTimeline
                  events={recentActivity.data?.data ?? []}
                  onSelect={handleTimelineSelect}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Column (1 Col): Security Overview & System Telemetry */}
        <div className="space-y-6">
          {/* Security Overview */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-3.5">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Security &amp; Threat Telemetry
              </h2>
            </div>
            <div className="divide-y divide-slate-100 px-5">
              {security.isLoading || !security.data ? (
                <div className="animate-pulse space-y-2 py-3">
                  <div className="h-3 w-full rounded bg-slate-200" />
                  <div className="h-3 w-full rounded bg-slate-200" />
                </div>
              ) : (
                <>
                  <SecurityRow label="Failed logins today" value={security.data.failedLoginsToday} warn />
                  <SecurityRow label="Locked accounts" value={security.data.lockedAccounts} warn />
                  <SecurityRow label="Suspicious events" value={security.data.suspiciousEvents} warn />
                  <SecurityRow label="Unique IPs today" value={security.data.uniqueIpsToday} />
                </>
              )}
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => navigate('/security')}
                className="text-xs font-bold text-sky-600 hover:text-sky-800 transition-colors"
              >
                Open Security Center →
              </button>
            </div>
          </div>

          {/* Quick Platform Shortcuts */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
              Management Shortcuts
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/activity?action=expired')}
                className="flex items-center justify-between w-full rounded-lg border border-amber-200 bg-amber-50/50 p-2.5 text-left text-xs font-bold text-amber-900 hover:bg-amber-100/70 transition-colors shadow-2xs"
              >
                <span>Review Expired Documents</span>
                <span>→</span>
              </button>

              <button
                onClick={() => navigate('/signatures')}
                className="flex items-center justify-between w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-left text-xs font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50/40 hover:text-sky-800 transition-colors shadow-2xs"
              >
                <span>Signature Audit Stream</span>
                <span>→</span>
              </button>

              <button
                onClick={() => navigate('/users')}
                className="flex items-center justify-between w-full rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-left text-xs font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50/40 hover:text-sky-800 transition-colors shadow-2xs"
              >
                <span>Platform Identity Directory</span>
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Detail Drawer */}
      <DocumentDetailDrawer
        open={docDrawerOpen}
        onClose={() => {
          setDocDrawerOpen(false)
          setSelectedEvent(null)
        }}
        event={selectedEvent}
        documentId={selectedEvent?.documentId || selectedEvent?.document_id}
      />
    </div>
  )
}

function ProgressBar({ label, count, pct, color }: { label: string; count: number; pct: number; color: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="font-mono text-slate-900 font-bold">
          {count} ({pct}%)
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-300`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
    </div>
  )
}

function SecurityRow({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between py-3 text-xs">
      <span className="font-medium text-slate-600">{label}</span>
      <span className={`font-mono font-bold ${warn && value > 0 ? 'text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200' : 'text-slate-900'}`}>
        {value}
      </span>
    </div>
  )
}
